import React, { ReactNode, useEffect, useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  AuditLogDTO,
  CourseDTO,
  CourseMaterialTypeDTO,
  CourseStatusDTO,
  CourseTypeDTO,
  MemberDTO,
  MemberTypeDTO,
  ProvinceDTO,
  RplDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import SimpleAlert from '../common/SimpleAlert';
import { useCourseTypes } from '../../hooks/useCourseTypeApi';
import { useCourseMaterialTypeApi } from '../../hooks/useCourseMaterialTypeApi';
import { useProvinces } from '../../hooks/useProvinceApi';
import MapboxAutocomplete from '../common/formElements/MapboxAutocomplete';
import { CourseMaterialTypeConstants } from '../../constants/CourseMaterialTypeConstansts';
import { useMemberApi } from '../../hooks/useMemberApi';
import { getMemberId, getRoles } from '../../utils/tokenUtils';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import AuditLogTable from '../tables/AuditLogTable';
import { useAuditLogApi } from '../../hooks/useAuditLogApi';
import { useCourseStatusApi } from '../../hooks/useCourseStatusApi';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';
import { CourseTypeConstants } from '../../constants/CourseTypeConstants';
import { useMemberTypes } from '../../hooks/useMemberTypeApi';
import { useMemberCategories } from '../../hooks/useMemberCategoryApi';
import { usePaymentTypes } from '../../hooks/usePaymentTypeApi';
import { useFileApi } from '../../hooks/useFileApi';
import { MemberCategoryConstants } from '../../constants/MemberCategoryConstants';
import { RplStatusConstants } from '../../constants/RplStatusConstants';
import { CourseStatusConstants } from '../../constants/CourseStatusConstants';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useRplApi } from '../../hooks/useRplApi';
import { formatDateForInput } from '../../utils/dateUtils/formatDateForInput';
import { formatDateTimeForInput } from '../../utils/dateUtils/formatDateTimeForInput';
import { useEvaluationStatusApi } from '../../hooks/useEvaluationStatusApi';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import MemberDetails from '../common/MemberDetails';
import { getCourseFee } from '../../utils/courseUtils';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface CourseFormProps {
  initialValues: CourseDTO;
  onSubmit: (data: CourseDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

interface Policy {
  acceptPolicy: boolean;
  error: string | undefined;
}

const CourseForm: React.FC<CourseFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<CourseDTO>(initialValues);
  const { getFeedback } = useCourseApi();

  // Feedback HTML is stored in blob storage now, not returned inline with the Course DTO — fetch it on demand.
  useEffect(() => {
    if (!formData.id) return;
    getFeedback(formData.id)
      .then((feedback) => setFormData((prev) => ({ ...prev, feedback })))
      .catch(() => { });
  }, [formData.id]);

  const [selectedMaterialType, setSelectedMaterialType] = useState<string>(initialValues.courseMaterialTypeId || '');
  const [policy, setPolicy] = useState<Policy>({ acceptPolicy: false, error: undefined });
  const [questionsError, setQuestionsError] = useState<string | undefined>(undefined);
  const [auditCurrentPage, setAuditCurrentPage] = useState(1);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);
  const statusFieldName = 'courseStatusId';
  const roles = getRoles();
  const memberId = getMemberId();

  const FORM_SECTIONS = {
    ...(roles?.includes('Admin')
      ? {
        memberDetails: {
          id: 'member-details',
          title: 'Member Details',
        },
        userDetails: {
          id: 'user-details',
          title: 'User Details',
        },
        shippingDetails: {
          id: 'shipping-details',
          title: 'Shipping Details',
        },
        recordDetails: {
          id: 'record-details',
          title: 'Record Details',
        },
        paymentDetails: {
          id: 'payment-details',
          title: 'Payment Details',
        },
        feedback: {
          id: 'feedback',
          title: 'Feedback',
        },
        history: {
          id: 'history',
          title: 'History',
        },
      }
      : {}),
    reason: {
      id: 'reason',
      title: 'Reason for dedicated status application?',
    },
    applicationDetails: {
      id: 'application-details',
      title: 'Application Details',
    },
  } as const;

  const sections = Object.values(FORM_SECTIONS);

  // Add state for questions
  const [questions, setQuestions] = useState({
    questionA: false,
    questionB: false,
    questionC: false,
    questionD: false,
    questionE: false,
    questionF: false,
    questionG: false,
    questionH: false,
  });

  const validationRules: ValidationRules = {
    questionUse: [{ required: !roles?.includes('Admin'), message: 'Firearm average use is required.' },],
    courseTypeId: [{ required: true, message: 'Application type is required' }],
    courseMaterialTypeId: [{ required: true, message: 'Please select how you would like to receive your study material' }],
    courierStreet: [{ required: selectedMaterialType === CourseMaterialTypeConstants.PRINT, message: 'Courier street is required', },],
    courierSuburb: [{ required: selectedMaterialType === CourseMaterialTypeConstants.PRINT, message: 'Courier suburb is required', },],
    courierTown: [{ required: selectedMaterialType === CourseMaterialTypeConstants.PRINT, message: 'Courier town is required', },],
    courierProvinceId: [{ required: selectedMaterialType === CourseMaterialTypeConstants.PRINT, message: 'Courier province is required', },],
    courierCode: [{ required: selectedMaterialType === CourseMaterialTypeConstants.PRINT, message: 'Courier code is required', },],
  };

  const { errors, validate } = useFormValidation<CourseDTO>(validationRules);

  const hasSelectedQuestion =
    questions.questionA ||
    questions.questionB ||
    questions.questionC ||
    questions.questionD ||
    questions.questionE ||
    questions.questionF ||
    questions.questionG ||
    questions.questionH;

  const { uploadFile, getFileUrl } = useFileApi();
  const { search: searchCourses, isLoading: isLoadingCourses } = useCourseApi();
  const { search: searchRpls, isLoading: isLoadingRpls } = useRplApi();
  const [auditLogs, setAuditLogs] = useState<AuditLogDTO[]>([]);
  const { search: searchAuditLogs, isLoading: isLoadingAuditLogs } = useAuditLogApi();
  const [isCourseTypesFiltered, setIsCourseTypesFiltered] = useState<boolean>(false);

  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});

  const [courseTypes, setCourseTypes] = useState<CourseTypeDTO[]>([]);
  const { data: courseTypesData = [], isLoading: isLoadingCourseTypes } = useCourseTypes();
  const fetchCourseTypes = async () => {
    setCourseTypes(courseTypesData);
  };

  const [courseMaterialTypes, setCourseMaterialTypes] = useState<CourseMaterialTypeDTO[]>([]);
  const { search: searchCourseMaterialTypes, isLoading: isLoadingCourseMaterialTypes } = useCourseMaterialTypeApi();
  const fetchCourseMaterialTypes = async () => {
    try {
      const response = await searchCourseMaterialTypes({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setCourseMaterialTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching course material types', error);
    }
  };

  const { data: provinces, isLoading: isLoadingProvinces } = useProvinces();

  const [member, setMember] = useState<MemberDTO>();
  const { getById: getMemberById, isLoading: isLoadingMembers } = useMemberApi();
  const fetchMember = async () => {
    if (!memberId) return;

    try {
      const response = await getMemberById(memberId);

      if (response?.data) {
        const fetchedMember = response.data;
        setMember(fetchedMember);

        // Only populate courier details when creating (no ID) and no existing courier data
        if (
          !initialValues.id &&
          !initialValues.courierAddress &&
          !initialValues.courierStreet
        ) {
          setFormData((prev) => ({
            ...prev,
            courierAddress:
              fetchedMember.physicalAddress || prev.courierAddress,
            courierStreet:
              fetchedMember.physicalAddress?.split(',')[0] ||
              prev.courierStreet ||
              '',
            courierSuburb: fetchedMember.physicalSuburb || prev.courierSuburb,
            courierTown: fetchedMember.physicalTown || prev.courierTown,
            courierProvinceId:
              fetchedMember.provinceId || prev.courierProvinceId,
            courierCode: fetchedMember.physicalPostalCode || prev.courierCode,
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching member:', error);
    }
  };


  const { data: memberTypesData, isLoading: isLoadingMemberTypes } = useMemberTypes();

  const { data: memberCategoriesData, isLoading: isLoadingMemberCategories } = useMemberCategories();

  const { data: paymentTypesData, isLoading: isLoadingPaymentTypes } = usePaymentTypes();

  const [courseStatuses, setCourseStatuses] = useState<CourseStatusDTO[]>([]);
  const { search: searchCourseStatuses, isLoading: isLoadingCourseStatuses } = useCourseStatusApi();
  const fetchCourseStatuses = async () => {
    try {
      const response = await searchCourseStatuses({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setCourseStatuses(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Course Statuses', error);
    }
  };

  const [evaluationStatuses, setEvaluationStatuses] = useState<CourseStatusDTO[]>([]);
  const { search: searchEvaluationStatuses, isLoading: isLoadingEvaluationStatuses, } = useEvaluationStatusApi();
  const fetchEvalutationStatuses = async () => {
    try {
      const response = await searchEvaluationStatuses({
        page: 1,
        pageSize: 9999,
        sortBy: 'DisplayPosition',
        sortDescending: false,
      });
      setEvaluationStatuses(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Evaluation Statuses', error);
    }
  };

  useEffect(() => {
    if (initialValues.member) {
      // Editing existing course - use member data from course object
      setMember(initialValues.member);
    } else if (!initialValues.id && memberId) {
      // Creating new course - fetch member data to pre-populate courier details
      fetchMember();
    }
    // fetchProvinces(); // No longer needed - using cached data
    fetchCourseMaterialTypes();
  }, [memberId, initialValues.member, initialValues.id]);

  useEffect(() => {
    if (courseTypesData.length > 0) {
      setCourseTypes(courseTypesData);
      setIsCourseTypesFiltered(false);
    }
  }, [courseTypesData]);

  const filterCourseTypes = async () => {
    try {
      if (formData.id) {
        return;
      }

      if (member) {
        const courseResponse = await searchCourses({
          page: 1,
          pageSize: 9999,
          filters: {
            memberId: memberId,
            isDeleted: false,
          },
        });

        const rplResponse = await searchRpls({
          page: 1,
          pageSize: 9999,
          filters: {
            memberId: memberId,
            isDeleted: false,
          },
        });

        const existingCourseApplications: CourseDTO[] =
          courseResponse?.data?.results || [];
        const existingRplApplications: RplDTO[] =
          rplResponse?.data?.results || [];

        const existingApplicationTypeIds = [
          ...existingCourseApplications
            .filter((course) => course.courseStatusId !== CourseStatusConstants.DECLINED && course.courseStatusId !== CourseStatusConstants.FORFEITED)
            .map((course) => course.courseTypeId),
          ...existingRplApplications
            .filter((rpl) => rpl.rplStatusId !== RplStatusConstants.DECLINED && rpl.rplStatusId !== RplStatusConstants.DECLINED_WITH_FEEDBACK && rpl.rplStatusId !== RplStatusConstants.FORFEITED)
            .map((rpl) => rpl.courseTypeId),
        ].filter(Boolean);

        const memberCategoryId = member.memberCategoryId;
        let filteredCourseTypes: CourseTypeDTO[] = [];

        const hasHunterApplication = existingApplicationTypeIds.some(
          (courseTypeId) =>
            courseTypeId === CourseTypeConstants.DEDICATED_HUNTER ||
            courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        );

        const hasSportsPersonApplication = existingApplicationTypeIds.some(
          (courseTypeId) =>
            courseTypeId === CourseTypeConstants.DEDICATED_SPORTS_PERSON ||
            courseTypeId ===
            CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON,
        );

        if (
          memberCategoryId === MemberCategoryConstants.DEDICATED_HUNTER ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_OCCASIONAL_SPORTPERSON ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON
        ) {
          filteredCourseTypes = courseTypes.filter(
            (ct) =>
              ct.id === CourseTypeConstants.DEDICATED_SPORTS_PERSON &&
              !hasSportsPersonApplication,
          );
        } else if (
          memberCategoryId === MemberCategoryConstants.DEDICATED_SPORTPERSON ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_SPORTPERSON_AND_OCCASIONAL_HUNTER ||
          memberCategoryId ===
          MemberCategoryConstants.DEDICATED_HUNTER_AND_SPORTPERSON
        ) {
          filteredCourseTypes = courseTypes.filter(
            (ct) =>
              ct.id === CourseTypeConstants.DEDICATED_HUNTER &&
              !hasHunterApplication,
          );
        } else {
          filteredCourseTypes = courseTypes.filter((ct) => {
            if (existingApplicationTypeIds.includes(ct.id)) {
              return false;
            }

            if (
              ct.id === CourseTypeConstants.DEDICATED_HUNTER_AND_SPORTS_PERSON
            ) {
              return !hasHunterApplication && !hasSportsPersonApplication;
            }

            if (ct.id === CourseTypeConstants.DEDICATED_HUNTER) {
              return !hasHunterApplication;
            }

            if (ct.id === CourseTypeConstants.DEDICATED_SPORTS_PERSON) {
              return !hasSportsPersonApplication;
            }

            return true;
          });
        }

        setCourseTypes(filteredCourseTypes);
        setIsCourseTypesFiltered(true);
      }
    } catch (error) {
      console.error('Error filtering course types:', error);
    }
  };

  useEffect(() => {
    if (courseTypes && courseTypes.length > 0 && !isCourseTypesFiltered && member) {
      filterCourseTypes();
    }
  }, [courseTypes, member]);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      courseMaterialTypeId: selectedMaterialType,
    }));
  }, [selectedMaterialType]);

  useEffect(() => {
    if (formData.courseTypeId && courseTypes.length > 0) {
      const matchedCourseType = courseTypes.find(
        (ct) => ct.id === formData.courseTypeId
      );
      setFormData((prev) => ({
        ...prev,
        courseType: matchedCourseType,
      }));
    }
  }, [formData.courseTypeId]);

  useEffect(() => {
    if (formData.courseMaterialTypeId && courseMaterialTypes.length > 0) {
      const matchedMaterialType = courseMaterialTypes.find(
        (cmt) => cmt.id === formData.courseMaterialTypeId
      );
      setFormData((prev) => ({
        ...prev,
        courseMaterialType: matchedMaterialType,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        courseMaterialType: undefined,
      }));
    }
  }, [formData.courseMaterialTypeId, courseMaterialTypes]);

  useEffect(() => {
    if (roles?.includes('Admin')) {
      fetchCourseStatuses();
      fetchEvalutationStatuses();
    }
  }, []);

  const handleLocationSelect = (latitude: number, longitude: number, addressComponents?: any) => {
    const locationProvince = addressComponents?.region || '';

    let matchedProvinceId = '';
    if (locationProvince && provinces && provinces.length > 0) {
      const matchedProvince = provinces.find(
        (province) =>
          String(province.title)
            .toLowerCase()
            .includes(locationProvince.toLowerCase()) ||
          locationProvince
            .toLowerCase()
            .includes(String(province.title).toLowerCase()),
      );

      if (matchedProvince) {
        matchedProvinceId = matchedProvince.id ?? '';
      }
    }

    const addressParts = [
      addressComponents?.addressLine1,
      addressComponents?.addressLine2,
      addressComponents?.town,
      addressComponents?.postalCode,
    ]
      .filter(Boolean)
      .join(', ');

    setFormData((prev) => ({
      ...prev,
      courierProvinceId: matchedProvinceId,
      courierAddress: addressParts,
      courierStreet: addressComponents?.addressLine1 || '',
      courierSuburb: addressComponents?.addressLine2 || '',
      courierTown: addressComponents?.town || '',
      courierCode: addressComponents?.postalCode || '',
    }));
  };

  useEffect(() => {
    const fetchAuditLogs = async () => {
      if (initialValues.id) {
        try {
          const response = await searchAuditLogs({
            page: auditCurrentPage,
            pageSize: auditPageSize,
            sortBy: 'CreatedUtc',
            sortDescending: true,
            filters: {
              tableName: 'Course',
              recordId: initialValues.id,
            },
          });

          if (response && response?.success && response?.data) {
            setAuditLogs(response.data.results as AuditLogDTO[]);
            setAuditTotalPages(
              Math.ceil((response.data.totalCount ?? 0) / auditPageSize),
            );
          }
        } catch (error) {
          console.error('Error fetching audit logs:', error);
        }
      }
    };

    fetchAuditLogs();
  }, [initialValues.id, auditCurrentPage, auditPageSize]);

  const handlePopFileChange = (field: keyof CourseDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'courses',
        );
        if (response && response.success && response.data) {
          setFormData((prev) => ({
            ...prev,
            [field]: response.data,
          }));
        }
      } catch (error) {
        console.error('Error handling proof of payment file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  const getCourseFeeForMaterialType = (materialTypeId: string): number => {
    const dedicatedStatusFee = formData.courseType?.dedicatedStatusFee || 0;
    const materialType = courseMaterialTypes.find((cmt) => cmt.id === materialTypeId);
    const materialFee = materialType?.fee || 0;
    return dedicatedStatusFee + materialFee;
  };

  const handleSave = () => {
    const validationData = { ...formData, policy };

    if (!roles?.includes('Admin')) {
      if (!hasSelectedQuestion) {
        setQuestionsError(
          'Please select at least one reason for your application',
        );
        return;
      } else {
        setQuestionsError(undefined);
      }

      if (!policy.acceptPolicy) {
        setPolicy((prev) => ({
          ...prev,
          error: 'You must accept the refund and return policy.',
        }));
        return;
      } else {
        setPolicy((prev) => ({ ...prev, error: undefined }));
      }
    }

    if (validate(validationData as any)) {
      onSubmit(formData);
    }
  };

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      {roles?.includes('Admin') && FORM_SECTIONS.memberDetails && (
        <FormSection
          id={FORM_SECTIONS.memberDetails.id}
          title={FORM_SECTIONS.memberDetails.title}
        >
          <MemberDetails member={formData.member ?? {}} />
        </FormSection>
      )}

      {roles?.includes('User') && (
        <>
          <SimpleAlert
            variant="warning"
            message="Please note!
                    Course fees are determined by the specific course you enrol for and by the cost of the study material you select. Cost includes enrolment, study material, courier of study material (if applicable), evaluation, certification and administrative fees.

                    For any questions or topics relating to the Natshoot (NHSA) Course use the help  icon at the top right corner."
          />
          <FormSection
            id={FORM_SECTIONS.reason.id}
            title={FORM_SECTIONS.reason.title}
            columns={1}
          >
            <SimpleAlert
              variant="warning"
              message="Please complete below why you need Dedicated Hunter/Sports-person Status as required by Regulation 4(2)(a)(ii)."
            />
            {questionsError && (
              <SimpleAlert variant="error" message={questionsError} />
            )}
            <div className="my-5">
              <FormField
                id="questionA"
                label="A) I already have four firearms licensed on my name and need dedicated status so that I can apply to legally own more than four firearms?"
                type="checkbox"
                value={questions.questionA}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionB"
                label="B) I have inherited a number of firearms, which will cause me to have more than four firearms and I shall then be required to have dedicated status if I want to licence them all in my name?"
                type="checkbox"
                value={questions.questionB}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionC"
                label="C) I want to licence a semi-automatic rifle in my name and need dedicated status to apply for such a licence?"
                type="checkbox"
                value={questions.questionC}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionD"
                label="D) I want to licence a semi-automatic shotgun in my name and need dedicated status to apply for such a licence?"
                type="checkbox"
                value={questions.questionD}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionE"
                label="E) I need dedicated status so that I can apply to have more than two handguns licensed in my name?"
                type="checkbox"
                value={questions.questionE}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionF"
                label="F) I am under 21 years of age and need to have dedicated status in order to apply for a competency certificate to own firearms"
                type="checkbox"
                value={questions.questionF}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionG"
                label="G) I like to be prepared and am now finalising all study work I have to do to comply with all requirements of the FCA to be able to apply for more than four firearm licences or for a licence for a semi-automatic rifle or shotgun when that time comes?"
                type="checkbox"
                value={questions.questionG}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
              <FormField
                id="questionH"
                label="H) I want to be in a position where I comply with as many as possible FCA requirements to ascertain my continued legal firearm ownership?"
                type="checkbox"
                value={questions.questionH}
                onChange={(e) => handleChange(e, setQuestions)}
                required={false}
                helper="I Confirm?"
              />
            </div>

            <FormField
              id="questionUse"
              label="On average, how many times do you use your firearms per year"
              type="dropdown"
              value={formData.questionUse || ''}
              onChange={(e) => handleChange(e, setFormData)}
              placeholder="Enter Question Use"
              required={true}
              error={errors.questionUse}
              options={[
                { value: '1-3', label: '1 - 3 times a year' },
                { value: '3-5', label: '3 - 5 times a year' },
                { value: '5+', label: 'More than 5 times a year' },
              ]}
            />
          </FormSection>
          {(hasSelectedQuestion || formData.id) && (
            <FormSection
              id={FORM_SECTIONS.applicationDetails.id}
              title={FORM_SECTIONS.applicationDetails.title}
              columns={1}
            >
              <FormField
                id="courseTypeId"
                label="Select application type"
                type="dropdown"
                value={formData.courseTypeId || ''}
                onChange={(e) => handleChange(e, setFormData)}
                placeholder="Select Application Type"
                required={true}
                error={errors.courseTypeId}
                options={courseTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingCourseTypes}
              />

              {formData.courseTypeId && (
                <>
                  <label htmlFor="">
                    How would you like to receive your study material?
                  </label>
                  <div className="flex">
                    <div
                      className={`border rounded-md mr-5 my-5 p-5 hover:border-primary hover:cursor-pointer ${selectedMaterialType === CourseMaterialTypeConstants.PRINT
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200'
                        }`}
                      onClick={() =>
                        setSelectedMaterialType(
                          CourseMaterialTypeConstants.PRINT,
                        )
                      }
                    >
                      <div className="mb-5">
                        <input
                          className="mr-3"
                          type="radio"
                          name="materialType"
                          checked={
                            selectedMaterialType ===
                            CourseMaterialTypeConstants.PRINT
                          }
                          onChange={() =>
                            setSelectedMaterialType(
                              CourseMaterialTypeConstants.PRINT,
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="font-bold mr-3">
                          Print & Courier Study Material
                        </span>
                        <span className="font-bold">R {getCourseFeeForMaterialType(CourseMaterialTypeConstants.PRINT)} (Incl VAT)</span>
                      </div>
                      <span>
                        Hard copy printed and couriered to you. <span className="text-orange-500">(PLEASE ALLOW 2
                          - 5 DAYS FOR DELIVERY)</span>
                      </span>
                    </div>
                    <div
                      className={`border rounded-md my-5 p-5 hover:border-primary hover:cursor-pointer ${selectedMaterialType === CourseMaterialTypeConstants.DOWNLOAD
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-slate-200'
                        }`}
                      onClick={() =>
                        setSelectedMaterialType(
                          CourseMaterialTypeConstants.DOWNLOAD,
                        )
                      }
                    >
                      <div className="mb-5">
                        <input
                          className="mr-3"
                          type="radio"
                          name="materialType"
                          checked={
                            selectedMaterialType ===
                            CourseMaterialTypeConstants.DOWNLOAD
                          }
                          onChange={() =>
                            setSelectedMaterialType(
                              CourseMaterialTypeConstants.DOWNLOAD,
                            )
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className="font-bold mr-3">
                          Download Study Material
                        </span>
                        <span className="font-bold">R {getCourseFeeForMaterialType(CourseMaterialTypeConstants.DOWNLOAD)} (Incl VAT)</span>
                      </div>
                      <span>
                        Immediately download the study material <span className="text-orange-500">(NOT PRINTABLE)</span>
                      </span>
                    </div>
                  </div>

                  {errors.courseMaterialTypeId && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                      {errors.courseMaterialTypeId}
                    </p>
                  )}

                  {selectedMaterialType ===
                    CourseMaterialTypeConstants.PRINT && (
                      <>
                        <SimpleAlert
                          variant="warning"
                          message="Please complete the courier address below to have the printed material delivered. We can not be held responsible if the address is incorrect.
                                Our office will be in contact with you if your courier address is too far away from main and/or satellite hubs to be easily serviced by our courier."
                        />
                        <div className="my-5">
                          <MapboxAutocomplete
                            fieldId="courierStreet"
                            fieldLabel="Courier street name and number"
                            value={formData.courierStreet || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            onLocationSelect={handleLocationSelect}
                            placeholder="Enter Courier Street"
                            required={true}
                            error={errors.courierStreet}
                          />
                          <FormField
                            id="courierSuburb"
                            label="Courier suburb/estate"
                            type="text"
                            value={formData.courierSuburb || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Courier Suburb"
                            required={true}
                            error={errors.courierSuburb}
                          />
                          <FormField
                            id="courierTown"
                            label="Courier closest city/town"
                            type="text"
                            value={formData.courierTown || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Courier Town"
                            required={true}
                            error={errors.courierTown}
                          />
                          <FormField
                            id="courierProvinceId"
                            label="Courier Province"
                            type="dropdown"
                            value={formData.courierProvinceId || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Courier Province Id"
                            required={true}
                            error={errors.courierProvinceId}
                            options={(provinces || []).map((type) => ({
                              value: type.id ?? '',
                              label: type.title ?? '',
                            }))}
                            isLoading={isLoadingProvinces}
                          />
                          <FormField
                            id="courierCode"
                            label="Courier area postal Code"
                            type="text"
                            value={formData.courierCode || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Courier Code"
                            required={true}
                            error={errors.courierCode}
                          />
                          <FormField
                            id="courierNote"
                            label="Courier Note"
                            type="textarea"
                            value={formData.courierNote || ''}
                            onChange={(e) => handleChange(e, setFormData)}
                            placeholder="Enter Courier Note"
                            required={false}
                            error={errors.courierNote}
                          />
                        </div>
                      </>
                    )}
                </>
              )}

              <FormField
                id="acceptPolicy"
                label={<span>I have read and understand the <a className='underline' href='https://www.natshoot.co.za/return-and-refund-policy' target='_blank'>refund and return policy</a></span>}
                type="checkbox"
                value={policy.acceptPolicy}
                onChange={(e) => handleChange(e, setPolicy)}
                required={false}
                error={policy.error}
                helper="I Confirm?"
              />
            </FormSection>
          )}
        </>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.userDetails && (
        <FormSection
          id={FORM_SECTIONS.userDetails.id}
          title={FORM_SECTIONS.userDetails.title}
        >
          <FormField
            id="membershipId"
            label={`Membership of ${formData.member?.firstName}`}
            type="dropdown"
            value={formData.member?.memberTypeId || ''}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberTypeId: e.target.value,
                },
              }));
            }}
            placeholder="Select Membership"
            required={true}
            error={errors.courseStatusId}
            options={(memberTypesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberTypes}
          />
          <FormField
            id="membercategoryId"
            label={`FCA status of ${formData.member?.firstName}`}
            type="dropdown"
            value={formData.member?.memberCategoryId || ''}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  memberCategoryId: e.target.value,
                },
              }));
            }}
            placeholder="Select Membership"
            required={true}
            error={errors.courseStatusId}
            options={(memberCategoriesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingMemberCategories}
          />
          <FormField
            id="dateCertificateHunter"
            label="Certificate Date (Hunter)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateHunter)}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateHunter: e.target.value
                    ? new Date(e.target.value)
                    : undefined,
                } as MemberDTO,
              }));
            }}
            placeholder="Enter Hunter Certificate Date"
            required={false}
            error={errors.dateCertificateHunter}
          />
          <FormField
            id="dateCertificateSport"
            label="Certificate Date (Sport)"
            type="date"
            value={formatDateForInput(formData.member?.dateCertificateSport)}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                member: {
                  ...prev.member,
                  dateCertificateSport: e.target.value
                    ? new Date(e.target.value)
                    : undefined,
                } as MemberDTO,
              }));
            }}
            placeholder="Enter Sport Certificate Date"
            required={false}
            error={errors.dateCertificateSport}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.shippingDetails && (
        <FormSection
          id={FORM_SECTIONS.shippingDetails.id}
          title={FORM_SECTIONS.shippingDetails.title}
        >
          <MapboxAutocomplete
            fieldId="courierStreet"
            fieldLabel="Courier street name and number"
            value={formData.courierStreet || ''}
            onChange={(e) => handleChange(e, setFormData)}
            onLocationSelect={handleLocationSelect}
            placeholder="Enter Courier Street"
            required={true}
            error={errors.courierStreet}
          />
          <FormField
            id="courierSuburb"
            label="Courier suburb/estate"
            type="text"
            value={formData.courierSuburb || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Suburb"
            required={true}
            error={errors.courierSuburb}
          />
          <FormField
            id="courierTown"
            label="Courier closest city/town"
            type="text"
            value={formData.courierTown || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Town"
            required={true}
            error={errors.courierTown}
          />
          <FormField
            id="courierProvinceId"
            label="Courier Province"
            type="dropdown"
            value={formData.courierProvinceId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Province Id"
            required={true}
            error={errors.courierProvinceId}
            options={(provinces || []).map((type) => ({
              value: type.id ?? '',
              label: type.title ?? '',
            }))}
            isLoading={isLoadingProvinces}
          />
          <FormField
            id="courierCode"
            label="Courier area postal Code"
            type="text"
            value={formData.courierCode || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Code"
            required={true}
            error={errors.courierCode}
          />
          <FormField
            id="courierNote"
            label="Courier Note"
            type="textarea"
            value={formData.courierNote || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Note"
            required={false}
            error={errors.courierNote}
          />
          <FormField
            id="courierTrackingReference"
            label="Courier Tracking Reference"
            type="text"
            value={formData.courierTrackingReference || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Courier Tracking Reference"
            required={false}
            error={errors.courierTrackingReference}
          />

          <FormField
            id="timeMaterialShipped"
            label="Date & Time Material Shipped"
            type="datetime-local"
            value={formatDateTimeForInput(formData.timeMaterialShipped) || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Time Material Shipped"
            required={false}
            error={errors.timeMaterialShipped}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.recordDetails && (
        <FormSection
          id={FORM_SECTIONS.recordDetails.id}
          title={FORM_SECTIONS.recordDetails.title}
        >
          <FormField
            id="theoryDate"
            label="Theory Date"
            type="date"
            value={formData.theoryDate || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.theoryDate}
          />
          <FormField
            id="practicalDate"
            label="Practical Date"
            type="date"
            value={formData.practicalDate || ''}
            onChange={(e) => handleChange(e, setFormData)}
            required={false}
            error={errors.practicalDate}
          />
          <FormField
            id="evaluationStatusId"
            label="Evaluation Status"
            type="dropdown"
            value={formData.evaluationStatusId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Evaluation Status"
            required={false}
            error={errors.evaluationStatusId}
            options={evaluationStatuses.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingEvaluationStatuses}
          />
          <FormField
            id="evaluationScorePercentage"
            label="Evaluation Score Percentage"
            type="decimal"
            value={formData.evaluationScorePercentage ?? ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Evaluation Score Percentage"
            required={false}
            error={errors.evaluationScorePercentage}
          />
          <FormField
            id="evaluationScoreMark"
            label="Evaluation Score Mark"
            type="decimal"
            value={formData.evaluationScoreMark ?? ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Evaluation Score Mark"
            required={false}
            error={errors.evaluationScoreMark}
          />

          <FormField
            id="timeMaterialDownloaded"
            label="Date & Time Material Downloaded"
            type="datetime-local"
            value={formatDateTimeForInput(formData.timeMaterialDownloaded) || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Time Material Downloaded"
            required={false}
            error={errors.timeMaterialDownloaded}
          />

          <FormField
            id="notes"
            label="Notes"
            type="textarea"
            value={formData.note || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.paymentDetails && (
        <FormSection
          id={FORM_SECTIONS.paymentDetails.id}
          title={FORM_SECTIONS.paymentDetails.title}
        >
          <FormField
            id="paymentTypeId"
            label="Payment Type"
            type="dropdown"
            value={formData.paymentTypeId || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Select Payment Type"
            required={false}
            error={errors.paymentTypeId}
            options={(paymentTypesData || []).map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            isLoading={isLoadingPaymentTypes}
          />
          {formData.proofOfPaymentFileId && (
            <ImageLink
              fileId={formData.proofOfPaymentFileId}
              label="Proof of payment"
            />
          )}
          <FileUpload
            id="proofOfPaymentFileId"
            onChange={handlePopFileChange('proofOfPaymentFileId')}
            title="Proof of payment"
            accept={FileTypeConstants.ALL_SUPPORTED}
            required={false}
            error={errors.proofOfPaymentFileId}
            isUploading={uploadingFiles['proofOfPaymentFileId']}
          />
          <FormField
            id="amountPaid"
            label="Amount Paid"
            type="number"
            value={formData.amountPaid || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Amount Paid"
            required={false}
            error={errors.amountPaid}
          />
          <FormField
            id="paidDate"
            label="Paid Date"
            type="date"
            value={formData.paidDate || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Paid Date"
            required={false}
            error={errors.paidDate}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.feedback && (
        <FormSection
          id={FORM_SECTIONS.feedback.id}
          title={FORM_SECTIONS.feedback.title}
        >
          <FeedbackForm
            componentId={ComponentConstants.DEDICATED_COURSES}
            recordId={formData.id ?? ''}
            initialData={formData}
            componentStatuses={courseStatuses.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            onChange={(updatedData) => {
              setFormData((prev) => ({
                ...prev,
                ...updatedData,
              }));
            }}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.history && (
        <FormSection
          id={FORM_SECTIONS.history.id}
          title={FORM_SECTIONS.history.title}
        >
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default CourseForm;
